import { useSearchParams } from 'react-router-dom';
import { useDataTablePaginationOptions } from './dataTableI18n';

/**
 * Hook to manage DataTable pagination with URL parameter persistence.
 * 
 * @param paramName The name of the URL parameter to store the page number in.
 * @returns An object containing props to spread onto a DataTable component.
 */
export const useDataTablePagination = (paramName: string = 'tablePage') => {
    const [searchParams, setSearchParams] = useSearchParams();
    const paginationOptions = useDataTablePaginationOptions();
    
    const page = parseInt(searchParams.get(paramName) || '1');
    
    const onChangePage = (newPage: number) => {
        // Aus dem jeweils aktuellen Stand, nicht aus dem des Renderdurchlaufs: Sonst überschreibt
        // ein zweiter Schreiber im selben Tick — etwa eine Filterleiste, die ihre Auswahl in
        // dieselbe Adresse legt — den ersten. Siehe `useUrlPageState`.
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.set(paramName, newPage.toString());
                return next;
            },
            { replace: true },
        );
    };
    
    return {
        pagination: true,
        paginationDefaultPage: page,
        paginationComponentOptions: paginationOptions,
        onChangePage
    };
};


/**
 * URL-persistente Paginierung für die shadcn-`DataTable` (TanStack): liefert
 * `page`/`onPageChange` als 1-basierte Seite im selben URL-Parameter-Schema
 * wie `useDataTablePagination`, sodass migrierte Tabellen ihre Seite in der
 * Adresse behalten.
 */
export const useUrlPageState = (paramName: string = 'tablePage') => {
    const [searchParams, setSearchParams] = useSearchParams();

    const page = parseInt(searchParams.get(paramName) || '1');

    const onPageChange = (newPage: number) => {
        /**
         * Aus dem jeweils aktuellen Stand, nicht aus dem des Renderdurchlaufs.
         *
         * Das war ein echter Verlust und kein theoretischer: Die Filterleiste der Prüfliste schrieb
         * ihre Auswahl eine Zeit lang per `history.replaceState` am Router vorbei. Dieser Hook baute
         * die neue Adresse aus dem Router-Stand — der die Filter nie gesehen hatte — und ein
         * Blättern in der Tabelle löschte sie. Die Filter nehmen inzwischen denselben Weg; die
         * Funktionsform schützt zusätzlich gegen zwei Schreiber im selben Tick.
         */
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.set(paramName, newPage.toString());
                return next;
            },
            { replace: true },
        );
    };

    return { page, onPageChange };
};
